import React, { useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Loader2, Play, Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/config/api";
import BiChart from "./BiChart";
import DrillDownDialog from "./DrillDownDialog";

const MODES = [
  { id: "aggregation", label: "Group & count", hint: "Totals per rep, area, doctor, stage…" },
  { id: "time_series", label: "Trend over time", hint: "Track a metric by day, week or month" },
  { id: "comparison", label: "Compare periods", hint: "This period against the previous one" },
  { id: "relationship", label: "Relationships", hint: "Referrals from doctors a rep visited" },
];

const CHART_TYPES = ["table", "bar", "line", "pie"];
const pill = (active) =>
  `px-2.5 py-1 rounded-full border text-xs transition-colors ${
    active ? "bg-blue-600 border-blue-600 text-white" : "bg-background border-input hover:bg-muted"
  }`;

export const BiReportBuilder = ({ schema, initialConfig = null, onTemplateSaved }) => {
  const collections = Object.entries(schema.collections);
  const [mode, setMode] = useState(initialConfig?.report_type || "aggregation");
  const [collection, setCollection] = useState(initialConfig?.config?.collection || "pipeline_cases");
  const [groupBy, setGroupBy] = useState(initialConfig?.config?.group_by || ["current_stage"]);
  const [metrics, setMetrics] = useState(initialConfig?.config?.metrics || ["record_count"]);
  const [dimension, setDimension] = useState(initialConfig?.config?.dimension || "assigned_rep_name");
  const [period, setPeriod] = useState(initialConfig?.config?.period || "month");
  const [seriesBy, setSeriesBy] = useState(initialConfig?.config?.series_by || "");
  const [datePreset, setDatePreset] = useState(initialConfig?.config?.date_preset || "last_30_days");
  const [dateField, setDateField] = useState(initialConfig?.config?.date_field || "");
  const [filters, setFilters] = useState([{ field: "", value: "" }]);
  const [chartType, setChartType] = useState(initialConfig?.visualization || "bar");
  const [relQuery, setRelQuery] = useState("referrals_from_doctors_visited");
  const [relName, setRelName] = useState("");
  const [relDays, setRelDays] = useState(30);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDescription, setTemplateDescription] = useState("");

  const meta = schema.collections[collection];
  const filterMap = useMemo(
    () => Object.fromEntries((meta?.filter_fields || []).map((f) => [f.id, f])),
    [meta]
  );

  const changeCollection = (next) => {
    setCollection(next);
    const firstDim = schema.collections[next].dimensions[0]?.id;
    setGroupBy(firstDim ? [firstDim] : []);
    setDimension(firstDim || "");
    setMetrics(["record_count"]);
    setSeriesBy("");
    setDateField("");
    setFilters([{ field: "", value: "" }]);
    setResult(null);
  };

  const filterPayload = () =>
    Object.fromEntries(
      filters
        .filter((f) => f.field && String(f.value).trim() !== "")
        .map((f) => [f.field, filterMap[f.field]?.type === "number" ? Number(f.value) : f.value])
    );

  const buildConfig = () => {
    const base = { collection, filters: filterPayload(), date_preset: datePreset };
    if (dateField) base.date_field = dateField;
    if (mode === "aggregation") return { ...base, group_by: groupBy, metrics };
    if (mode === "time_series")
      return { ...base, metrics, period, series_by: seriesBy || null };
    if (mode === "comparison") return { ...base, dimension, metric: metrics[0] || "record_count" };
    return {};
  };

  const run = async () => {
    setLoading(true);
    try {
      if (mode === "relationship") {
        if (!relName.trim()) {
          toast.error(relQuery === "doctor_effectiveness" ? "Enter a doctor name" : "Enter a rep name");
          return;
        }
        const params =
          relQuery === "doctor_effectiveness"
            ? { doctor_name: relName.trim(), days: Number(relDays) }
            : { rep_name: relName.trim(), days: Number(relDays) };
        const { data } = await axios.post(
          `${API}/bi/relationship-query`,
          { query_type: relQuery, params },
          { withCredentials: true }
        );
        setResult({
          kind: "relationship",
          summary: data.summary,
          columns: ["label", "value"],
          rows: data.breakdown || [],
          records: data.records || [],
        });
      } else if (mode === "aggregation") {
        const { data } = await axios.post(`${API}/bi/aggregate`, buildConfig(), { withCredentials: true });
        setResult({
          kind: "aggregation",
          columns: [...data.group_by, ...data.metrics.map((m) => m.id)],
          valueKeys: data.metrics.map((m) => m.id),
          rows: data.rows,
          totals: data.totals,
          metrics: data.metrics,
          summary: { Rows: data.row_count, From: data.date_from || "all time", To: data.date_to || "today" },
        });
      } else if (mode === "time_series") {
        const { data } = await axios.post(`${API}/bi/time-series`, buildConfig(), { withCredentials: true });
        setResult({
          kind: "time_series",
          columns: ["period", ...data.series],
          valueKeys: data.series,
          rows: data.points,
          summary: { Metric: data.metric.label, Period: data.period, From: data.date_from, To: data.date_to },
        });
      } else {
        const { data } = await axios.post(`${API}/bi/compare`, buildConfig(), { withCredentials: true });
        setResult({
          kind: "comparison",
          columns: ["label", "current", "previous", "change", "change_pct"],
          valueKeys: ["current", "previous"],
          rows: data.rows,
          summary: {
            Metric: data.metric.label,
            "Current period": `${data.current_period.from} → ${data.current_period.to}`,
            "Previous period": `${data.previous_period.from} → ${data.previous_period.to}`,
          },
        });
      }
      toast.success("Report generated");
    } catch (error) {
      console.error("BI report failed", error);
      toast.error(error?.response?.data?.detail || "Could not generate that report");
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      const response = await axios.post(
        `${API}/bi/export/xlsx`,
        {
          title: `${meta?.label || collection} — ${MODES.find((m) => m.id === mode)?.label}`,
          columns: result.columns,
          rows: result.rows.map((row) => result.columns.map((column) => row[column] ?? "")),
          summary: result.summary || {},
          chart: chartType === "table" ? null : chartType,
        },
        { withCredentials: true, responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `bi-report-${new Date().toISOString().split("T")[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Excel file downloaded");
    } catch (error) {
      console.error("Excel export failed", error);
      toast.error("Could not build the Excel file");
    }
  };

  const saveTemplate = async () => {
    if (!templateName.trim()) {
      toast.error("Give the template a name");
      return;
    }
    try {
      await axios.post(
        `${API}/bi/templates`,
        {
          name: templateName.trim(),
          description: templateDescription.trim() || null,
          report_type: mode,
          visualization: chartType,
          config: buildConfig(),
        },
        { withCredentials: true }
      );
      toast.success("Template saved");
      setSaveOpen(false);
      setTemplateName("");
      setTemplateDescription("");
      onTemplateSaved && onTemplateSaved();
    } catch (error) {
      console.error("Template save failed", error);
      toast.error("Could not save the template");
    }
  };

  const onRowSelect = (row) => {
    if (mode === "relationship") return;
    const keys = mode === "aggregation" ? groupBy : mode === "comparison" ? [dimension] : [];
    if (!keys.length) return;
    const drillFilters = {};
    keys.forEach((key) => {
      drillFilters[key] = mode === "comparison" ? row.label : row[key];
    });
    setDrill({
      title: `Records behind ${Object.values(drillFilters).join(" · ")}`,
      request: { collection, filters: { ...filterPayload(), ...drillFilters }, date_preset: datePreset, date_field: dateField || undefined },
    });
  };

  return (
    <div className="space-y-6" data-testid="bi-report-builder">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Report builder</CardTitle>
          <CardDescription>Pick what to measure, how to slice it and how to show it.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
            {MODES.map((option) => (
              <button
                key={option.id}
                onClick={() => {
                  setMode(option.id);
                  setResult(null);
                  setChartType(option.id === "time_series" ? "line" : option.id === "aggregation" ? "bar" : "table");
                }}
                className={`text-left p-3 rounded-lg border-2 transition-all ${
                  mode === option.id
                    ? "border-blue-600 bg-blue-50 dark:bg-blue-950/40"
                    : "border-border hover:border-blue-300"
                }`}
                data-testid={`bi-mode-${option.id}`}
              >
                <p className="text-sm font-semibold">{option.label}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{option.hint}</p>
              </button>
            ))}
          </div>

          {mode === "relationship" ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Question</Label>
                <select
                  value={relQuery}
                  onChange={(e) => setRelQuery(e.target.value)}
                  className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                  data-testid="bi-relationship-select"
                >
                  {schema.relationship_queries.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-xs">{relQuery === "doctor_effectiveness" ? "Doctor" : "Rep"}</Label>
                <Input
                  value={relName}
                  onChange={(e) => setRelName(e.target.value)}
                  placeholder={relQuery === "doctor_effectiveness" ? "e.g. Pienaar" : "e.g. Anel"}
                  className="mt-1"
                  data-testid="bi-relationship-name"
                />
              </div>
              <div>
                <Label className="text-xs">Look back (days)</Label>
                <Input
                  type="number"
                  value={relDays}
                  onChange={(e) => setRelDays(e.target.value)}
                  className="mt-1"
                  data-testid="bi-relationship-days"
                />
              </div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Data source</Label>
                  <select
                    value={collection}
                    onChange={(e) => changeCollection(e.target.value)}
                    className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                    data-testid="bi-collection-select"
                  >
                    {collections.map(([id, value]) => (
                      <option key={id} value={id}>
                        {value.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Period</Label>
                  <select
                    value={datePreset}
                    onChange={(e) => setDatePreset(e.target.value)}
                    className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                    data-testid="bi-preset-select"
                  >
                    {schema.date_presets.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                {(meta?.date_fields || []).length > 1 && (
                  <div>
                    <Label className="text-xs">Date measured on</Label>
                    <select
                      value={dateField}
                      onChange={(e) => setDateField(e.target.value)}
                      className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                      data-testid="bi-datefield-select"
                    >
                      <option value="">{meta.default_date_field} (default)</option>
                      {meta.date_fields.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Show as</Label>
                  <select
                    value={chartType}
                    onChange={(e) => setChartType(e.target.value)}
                    className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                    data-testid="bi-charttype-select"
                  >
                    {CHART_TYPES.map((option) => (
                      <option key={option} value={option}>
                        {option === "table" ? "Table" : `${option[0].toUpperCase()}${option.slice(1)} chart`}
                      </option>
                    ))}
                  </select>
                </div>
                {mode === "time_series" && (
                  <div>
                    <Label className="text-xs">Bucket</Label>
                    <select
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                      data-testid="bi-period-select"
                    >
                      <option value="day">Daily</option>
                      <option value="week">Weekly</option>
                      <option value="month">Monthly</option>
                    </select>
                  </div>
                )}
                {mode === "time_series" && (
                  <div>
                    <Label className="text-xs">Split lines by</Label>
                    <select
                      value={seriesBy}
                      onChange={(e) => setSeriesBy(e.target.value)}
                      className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                      data-testid="bi-seriesby-select"
                    >
                      <option value="">Nothing (one line)</option>
                      {(meta?.dimensions || [])
                        .filter((d) => d.kind === "field")
                        .map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
                {mode === "comparison" && (
                  <div>
                    <Label className="text-xs">Compare by</Label>
                    <select
                      value={dimension}
                      onChange={(e) => setDimension(e.target.value)}
                      className="mt-1 h-10 w-full px-2 rounded-md border border-input bg-background text-sm"
                      data-testid="bi-dimension-select"
                    >
                      {(meta?.dimensions || [])
                        .filter((d) => d.kind === "field")
                        .map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                    </select>
                  </div>
                )}
              </div>

              {mode === "aggregation" && (
                <div>
                  <Label className="text-xs">Group by</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="bi-groupby-pills">
                    {(meta?.dimensions || []).map((option) => (
                      <button
                        key={option.id}
                        onClick={() =>
                          setGroupBy((prev) =>
                            prev.includes(option.id) ? prev.filter((d) => d !== option.id) : [...prev, option.id]
                          )
                        }
                        className={pill(groupBy.includes(option.id))}
                        data-testid={`bi-groupby-${option.id}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode !== "comparison" && (
                <div>
                  <Label className="text-xs">Measure</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="bi-measure-pills">
                    {(meta?.measures || []).map((option) => (
                      <button
                        key={option.id}
                        onClick={() =>
                          setMetrics((prev) =>
                            prev.includes(option.id)
                              ? prev.filter((m) => m !== option.id) || ["record_count"]
                              : [...prev, option.id]
                          )
                        }
                        className={pill(metrics.includes(option.id))}
                        data-testid={`bi-measure-${option.id}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode === "comparison" && (
                <div>
                  <Label className="text-xs">Measure</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {(meta?.measures || []).map((option) => (
                      <button
                        key={option.id}
                        onClick={() => setMetrics([option.id])}
                        className={pill(metrics[0] === option.id)}
                        data-testid={`bi-measure-${option.id}`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs">Filters (optional)</Label>
                <div className="space-y-2 mt-1.5">
                  {filters.map((filter, index) => (
                    <div key={index} className="flex flex-col sm:flex-row gap-2">
                      <select
                        value={filter.field}
                        onChange={(e) =>
                          setFilters((prev) =>
                            prev.map((f, i) => (i === index ? { ...f, field: e.target.value } : f))
                          )
                        }
                        className="h-10 flex-1 px-2 rounded-md border border-input bg-background text-sm"
                        data-testid={`bi-filter-field-${index}`}
                      >
                        <option value="">No filter</option>
                        {(meta?.filter_fields || []).map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <Input
                        value={filter.value}
                        onChange={(e) =>
                          setFilters((prev) =>
                            prev.map((f, i) => (i === index ? { ...f, value: e.target.value } : f))
                          )
                        }
                        placeholder="equals…"
                        className="flex-1"
                        data-testid={`bi-filter-value-${index}`}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFilters((prev) => (prev.length === 1 ? [{ field: "", value: "" }] : prev.filter((_, i) => i !== index)))}
                        data-testid={`bi-filter-remove-${index}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1"
                    onClick={() => setFilters((prev) => [...prev, { field: "", value: "" }])}
                    data-testid="bi-add-filter-btn"
                  >
                    <Plus className="h-4 w-4" />
                    Add filter
                  </Button>
                </div>
              </div>
            </>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={run} disabled={loading} className="gap-2" data-testid="bi-run-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {loading ? "Running…" : "Run report"}
            </Button>
            {result && result.kind !== "relationship" && (
              <>
                <Button variant="outline" className="gap-2" onClick={exportExcel} data-testid="bi-export-btn">
                  <Download className="h-4 w-4" />
                  Export to Excel
                </Button>
                <Button variant="outline" className="gap-2" onClick={() => setSaveOpen(true)} data-testid="bi-save-template-btn">
                  <Save className="h-4 w-4" />
                  Save as template
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {result && (
        <Card data-testid="bi-result-card">
          <CardHeader>
            <CardTitle className="text-base">Result</CardTitle>
            <CardDescription>
              {result.kind === "relationship"
                ? "Answer below — the matching records are listed underneath"
                : chartType === "table"
                ? "Click a row to see the records behind it"
                : "Click a bar or slice to see the records behind it"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {result.summary && (
              <div className="flex flex-wrap gap-2" data-testid="bi-result-summary">
                {Object.entries(result.summary).map(([key, value]) => (
                  <Badge key={key} variant="secondary" className="text-[11px]">
                    {key}: {String(value)}
                  </Badge>
                ))}
              </div>
            )}
            <BiChart
              type={result.kind === "relationship" ? "table" : chartType}
              columns={result.columns}
              rows={result.rows}
              valueKeys={result.valueKeys}
              onSelect={result.kind === "relationship" ? null : onRowSelect}
            />
            {result.kind === "relationship" && result.records?.length > 0 && (
              <div className="pt-2">
                <p className="text-xs font-semibold text-muted-foreground mb-2">
                  {result.records.length} matching record(s)
                </p>
                <BiChart type="table" columns={Object.keys(result.records[0])} rows={result.records} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="bg-white dark:bg-slate-900" data-testid="bi-save-template-dialog">
          <DialogHeader>
            <DialogTitle>Save as template</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g. Weekly rep scorecard"
                className="mt-1"
                data-testid="bi-template-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Description (optional)</Label>
              <Input
                value={templateDescription}
                onChange={(e) => setTemplateDescription(e.target.value)}
                className="mt-1"
                data-testid="bi-template-description-input"
              />
            </div>
            <Button onClick={saveTemplate} className="w-full" data-testid="bi-template-save-confirm">
              Save template
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DrillDownDialog
        open={Boolean(drill)}
        onOpenChange={(open) => !open && setDrill(null)}
        title={drill?.title}
        request={drill?.request}
      />
    </div>
  );
};

export default BiReportBuilder;
